import { useState, useEffect } from "react";
import { supabase } from "../../lib/supabase";
import { formatDate } from "../../utils/dateUtils";
import DeleteConfirmationModal from "../../components/clientDetail/sections/functions/DeleteConfirmationModal";
import AssignTaskDropdown from "../../components/websiteDetail/AssignTaskDropdown";
import WebsiteDropdown from "../../components/WebsiteDropdown";
import WebsiteTaskDetailModal from "./WebsiteTaskDetailModal";
import { Button, Badge, Checkbox, Dropdown, Icon, IconButton, Input } from "../../components/ui";
import type { BadgeTone } from "../../components/ui";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import confetti from 'canvas-confetti';

interface Task {
  id: string;
  title: string;
  description?: string;
  status: string;
  due_date?: string;
  website_id?: string;
  website_url?: string;
  website_title?: string;
  priority: number;
  created_at: string;
  updated_at: string;
  assigned_to?: string | null;
  assigned_to_name?: string | null;
  metadata?: Record<string, unknown>;
}

const STATUS_TONES: Record<string, BadgeTone> = {
  Scheduled: 'info',
  'In Progress': 'warn',
  Complete: 'up',
  Submitted: 'accent',
  Blocked: 'down',
};

const statusTone = (status: string): BadgeTone => STATUS_TONES[status] || 'idle';

interface SortableTaskItemProps {
  task: Task;
  onClick: (task: Task) => void;
  onStatusChange: (id: string, status: string) => void;
  onAssign: (id: string, userId: string, userName: string) => void;
  onDelete: (id: string) => void;
}

function SortableTaskItem({
  task,
  onClick,
  onStatusChange,
  onAssign,
  onDelete
}: SortableTaskItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: task.id });
  const [isAssignDropdownOpen, setIsAssignDropdownOpen] = useState(false);

  const isComplete = task.status === 'Complete';

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 100 : isAssignDropdownOpen ? 99 : 1,
    opacity: isDragging ? 0.8 : 1,
    background: 'var(--surface-card)',
    border: '1px solid ' + (isDragging ? 'var(--border-strong)' : 'var(--border-hairline)'),
    borderRadius: 'var(--radius-md)',
    boxShadow: isDragging ? 'var(--shadow-hover)' : 'none',
    padding: '12px 14px',
    marginBottom: '8px',
    cursor: 'move',
    position: 'relative',
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners} className="hover-wash">
      {/* Phones stack the assign / status / delete controls under the task;
          side by side they left the title ~40px */}
      <div className="flex flex-col md:flex-row justify-between md:items-start" style={{ gap: 'var(--space-3)' }}>
        <div className="flex-1" style={{ minWidth: 0 }}>
          <div className="flex items-center" style={{ gap: '10px', marginBottom: '6px' }}>
            <span style={{ position: 'relative', zIndex: 10, display: 'inline-flex' }} onClick={(e) => e.stopPropagation()}>
              <Checkbox
                checked={isComplete}
                onChange={(checked) => onStatusChange(task.id, checked ? 'Complete' : 'Scheduled')}
              />
            </span>
            <h3
              style={{
                margin: 0,
                fontSize: 'var(--text-base)',
                fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'],
                color: isComplete ? 'var(--text-faint)' : 'var(--text-body)',
                textDecoration: isComplete ? 'line-through' : 'none',
                transition: 'color var(--dur-base) var(--ease-out)',
              }}
            >
              {task.title}
            </h3>
          </div>

          {task.description && (
            <p
              className="line-clamp-2"
              style={{ margin: '0 0 8px 26px', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}
            >
              {task.description}
            </p>
          )}

          <div className="flex items-center flex-wrap" style={{ gap: '8px', marginLeft: '26px' }}>
            <Badge tone={statusTone(task.status)} dot>{task.status}</Badge>

            {task.due_date && (
              <span className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                <Icon name="calendar-days" size={12} />
                Due {formatDate(task.due_date, 'MMM d')}
              </span>
            )}

            {task.assigned_to_name && (
              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                <Icon name="user" size={12} />
                {task.assigned_to_name}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center" style={{ gap: '8px', position: 'relative', zIndex: 10 }}>
          {/* Site name — mono, right aligned */}
          {task.website_title && (
            <span
              className="t-num hidden md:inline"
              style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)', whiteSpace: 'nowrap' }}
            >
              {task.website_title}
            </span>
          )}

          {/* Assign Dropdown */}
          <AssignTaskDropdown
            taskId={task.id}
            assignedUserId={task.assigned_to || null}
            assignedUserName={task.assigned_to_name || null}
            onAssign={(userId, userName) => onAssign(task.id, userId, userName)}
            onToggleOpen={(open) => setIsAssignDropdownOpen(open)}
          />

          {/* Status Dropdown */}
          <Dropdown
            size="sm"
            options={[
              { value: 'Scheduled', label: 'Scheduled' },
              { value: 'In Progress', label: 'In Progress' },
              { value: 'Blocked', label: 'Blocked' },
              { value: 'Complete', label: 'Complete' },
              { value: 'Submitted', label: 'Submitted' }
            ]}
            value={task.status}
            onChange={(e) => onStatusChange(task.id, e.target.value)}
            placeholder="Status..."
            style={{ width: '132px' }}
          />

          {/* Delete Button */}
          <span onClick={(e) => e.stopPropagation()}>
            <IconButton
              icon="trash-2"
              size="sm"
              label="Delete Task"
              onClick={() => onDelete(task.id)}
              style={{ color: 'var(--danger-400)' }}
            />
          </span>
        </div>

        {/* Clickable overlay for the entire card */}
        <div
          className="absolute inset-0 cursor-pointer"
          style={{ zIndex: 5 }}
          onClick={() => onClick(task)}
        ></div>
      </div>
    </div>
  );
}

interface WebsiteTasksPanelProps {
  searchTerm?: string;
}

export default function WebsiteTasksPanel({ searchTerm = '' }: WebsiteTasksPanelProps) {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [filteredTasks, setFilteredTasks] = useState<Task[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddingTask, setIsAddingTask] = useState(false);
  const [isReordering, setIsReordering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [localSearchTerm, setLocalSearchTerm] = useState('');

  // Task form state
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    status: 'Scheduled',
    due_date: formatDate(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
    website_id: '',
    priority: 0
  });

  // For the "Add Task" button
  const [isSubmitting, setIsSubmitting] = useState(false);

  // For delete confirmation
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({
    title: "",
    status: "",
    due_date: "",
    description: ""
  });

  // For website dropdown
  const [websites, setWebsites] = useState<{ id: string; url: string; title: string | null }[]>([]);

  // Set up drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px of movement required before drag starts
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fetch tasks when component mounts
  useEffect(() => {
    fetchTasks();
    fetchWebsites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter tasks when search term or status filter changes
  useEffect(() => {
    const effectiveSearchTerm = searchTerm || localSearchTerm;

    let filtered = [...tasks];

    // Apply status filter if set
    if (statusFilter) {
      filtered = filtered.filter(task => task.status === statusFilter);
    }

    // Apply search term if set
    if (effectiveSearchTerm) {
      filtered = filtered.filter(task =>
        task.title.toLowerCase().includes(effectiveSearchTerm.toLowerCase()) ||
        (task.description && task.description.toLowerCase().includes(effectiveSearchTerm.toLowerCase())) ||
        (task.website_title && task.website_title.toLowerCase().includes(effectiveSearchTerm.toLowerCase()))
      );
    }

    // Sort by priority
    filtered.sort((a, b) => a.priority - b.priority);

    setFilteredTasks(filtered);
  }, [tasks, statusFilter, searchTerm, localSearchTerm]);

  // Fetch websites for the dropdown
  const fetchWebsites = async () => {
    try {
      const { data, error } = await supabase
        .from('websites')
        .select('id, url, title')
        .order('title', { ascending: true });

      if (error) throw error;

      setWebsites(data || []);

      // Set default website if available
      if (data && data.length > 0 && !newTask.website_id) {
        setNewTask({ ...newTask, website_id: data[0].id });
      }
    } catch (err) {
      console.error('Error fetching websites:', err);
    }
  };

  // Fetch all website tasks
  const fetchTasks = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('website_tasks')
        .select(`
          *,
          websites (
            id,
            url,
            title
          )
        `)
        .order('priority');

      if (error) throw error;

      type JoinedRow = Task & { websites?: { url?: string; title?: string } };
      const formattedTasks = ((data || []) as unknown as JoinedRow[]).map((task) => ({
        ...task,
        website_url: task.websites?.url,
        website_title: task.websites?.title || task.websites?.url
      }));

      setTasks(formattedTasks);
    } catch (err) {
      console.error('Error fetching website tasks:', err);
      setError('Failed to load tasks. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddTask = async () => {
    if (!newTask.title.trim() || !newTask.website_id) return;

    setIsSubmitting(true);
    setError(null);

    try {
      // Get the highest current priority
      const highestPriority = tasks.length > 0
        ? Math.max(...tasks.map(t => t.priority))
        : -1;

      // Add task with next priority
      const { data, error } = await supabase
        .from('website_tasks')
        .insert({
          title: newTask.title,
          description: newTask.description || null,
          status: newTask.status,
          due_date: newTask.due_date || null,
          website_id: newTask.website_id,
          priority: highestPriority + 1
        })
        .select(`
          *,
          websites (
            id,
            url,
            title
          )
        `)
        .single();

      if (error) throw error;

      const newTaskWithWebsite: Task = {
        ...(data as unknown as Task & { websites?: { url?: string; title?: string } }),
        website_url: data.websites?.url,
        website_title: data.websites?.title || data.websites?.url
      };

      setTasks([...tasks, newTaskWithWebsite]);

      // Reset form
      setNewTask({
        title: '',
        description: '',
        status: 'Scheduled',
        due_date: formatDate(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
        website_id: newTask.website_id, // Keep the same website selected
        priority: 0
      });

      setIsAddingTask(false);
    } catch (err) {
      console.error('Error adding task:', err);
      setError('Failed to add task. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const sendCompletionNotification = async (taskData: Record<string, unknown>) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch('/.netlify/functions/task-complete-notification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token ?? ''}`,
        },
        body: JSON.stringify({ task: taskData })
      });

      if (!response.ok) {
        console.error('Failed to send notification');
      }
    } catch (error) {
      console.error('Error sending notification:', error);
    }
  };

  const handleStatusChange = async (taskId: string, newStatus: string) => {
    const task = tasks.find(t => t.id === taskId);
    const oldStatus = task?.status;

    try {
      setError(null);

      const { error } = await supabase
        .from('website_tasks')
        .update({ status: newStatus })
        .eq('id', taskId);

      if (error) throw error;

      // Update local state
      setTasks(tasks.map(task =>
        task.id === taskId ? { ...task, status: newStatus } : task
      ));

      // Fire confetti if task was just completed or submitted to the client
      if ((newStatus === 'Complete' || newStatus === 'Submitted') && oldStatus !== newStatus) {
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
          colors: ['#d95636', '#fbbf24', '#10b981', '#3b82f6', '#8b5cf6']
        });
      }

      // Send email notification (fire and forget) — Complete only, not Submitted
      if (newStatus === 'Complete' && oldStatus !== 'Complete' && task) {
        sendCompletionNotification({
          id: task.id,
          title: task.title,
          description: task.description,
          assigned_to_name: task.assigned_to_name,
          due_date: task.due_date
        });
      }
    } catch (err) {
      console.error('Error updating task status:', err);
      setError('Failed to update task status.');
    }
  };

  const handleAssignTask = async (taskId: string, userId: string, userName: string) => {
    try {
      setError(null);

      const { error } = await supabase.rpc('assign_website_task_to_user', {
        p_website_task_id: taskId,
        p_user_id: userId
      });

      if (error) throw error;

      // Update local state
      setTasks(tasks.map(task =>
        task.id === taskId ? {
          ...task,
          assigned_to: userId,
          assigned_to_name: userName
        } : task
      ));
    } catch (err) {
      console.error('Error assigning task:', err);
      setError('Failed to assign task. Please try again.');
    }
  };

  const handleOpenTask = async (task: Task) => {
    try {
      // Fetch the complete task data to ensure we have all fields including notes
      const { data: fullTaskData, error: taskError } = await supabase
        .from('website_tasks')
        .select('*')
        .eq('id', task.id)
        .single();

      if (taskError) {
        console.error('Error fetching complete task data:', taskError);
        throw taskError;
      }

      // Merge with template data if needed
      const enhancedTask = fullTaskData as unknown as Task;

      setSelectedTask(enhancedTask);
      setEditForm({
        title: enhancedTask.title,
        status: enhancedTask.status,
        due_date: enhancedTask.due_date ? formatDate(enhancedTask.due_date, 'yyyy-MM-dd') : '',
        description: enhancedTask.description || ''
      });
      setIsTaskModalOpen(true);
      setEditMode(false);
    } catch (error) {
      console.error('Error opening task:', error);
      setError('Failed to load task details. Please try again.');
    }
  };

  const handleSaveTaskChanges = async () => {
    if (!selectedTask || !editForm) return;

    try {
      // Track original status before update
      const originalStatus = selectedTask.status;

      const { error } = await supabase
        .from("website_tasks")
        .update({
          title: editForm.title,
          status: editForm.status,
          due_date: editForm.due_date || null,
          description: editForm.description
        })
        .eq("id", selectedTask.id);

      if (error) throw error;

      // Update local state
      setTasks(tasks.map(task =>
        task.id === selectedTask.id ? {
          ...task,
          title: editForm.title,
          status: editForm.status,
          due_date: editForm.due_date || undefined,
          description: editForm.description
        } : task
      ));

      // Check if status changed to Complete and send notification
      const statusChangedToComplete = originalStatus !== 'Complete' && editForm.status === 'Complete';

      if (statusChangedToComplete) {
        const { data: { session } } = await supabase.auth.getSession();
        fetch('/.netlify/functions/task-complete-notification', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session?.access_token ?? ''}`,
          },
          body: JSON.stringify({
            task: {
              id: selectedTask.id,
              title: editForm.title,
              description: editForm.description,
              assigned_to_name: selectedTask.assigned_to_name,
              due_date: editForm.due_date
            }
          })
        }).catch(err => console.error('Notification error:', err));
      }

      setEditMode(false);
      setIsTaskModalOpen(false);
    } catch (err) {
      console.error("Error updating task:", err);
      setError("Failed to update task. Please try again.");
    }
  };

  const openDeleteModal = (taskId: string) => {
    setTaskToDelete(taskId);
    setDeleteModalOpen(true);
  };

  const handleDeleteTask = async () => {
    if (!taskToDelete) return;

    setIsDeleting(true);

    try {
      const { error } = await supabase
        .from('website_tasks')
        .delete()
        .eq('id', taskToDelete);

      if (error) throw error;

      setTasks(tasks.filter(task => task.id !== taskToDelete));
    } catch (err) {
      console.error('Error deleting task:', err);
      setError('Failed to delete task.');
    } finally {
      setIsDeleting(false);
      setDeleteModalOpen(false);
      setTaskToDelete(null);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    setIsReordering(true);

    try {
      // Find indices
      const oldIndex = filteredTasks.findIndex(task => task.id === active.id);
      const newIndex = filteredTasks.findIndex(task => task.id === over.id);

      if (oldIndex === -1 || newIndex === -1) return;

      // Calculate the new order
      const reorderedTasks = arrayMove([...filteredTasks], oldIndex, newIndex);

      // Update priorities
      const updates = reorderedTasks.map((task, index) => ({
        id: task.id,
        priority: index
      }));

      // Update local state immediately
      const updatedTasks = [...tasks];
      updates.forEach(update => {
        const index = updatedTasks.findIndex(t => t.id === update.id);
        if (index !== -1) {
          updatedTasks[index] = { ...updatedTasks[index], priority: update.priority };
        }
      });
      setTasks(updatedTasks);

      // Update database
      for (const update of updates) {
        await supabase
          .from('website_tasks')
          .update({ priority: update.priority })
          .eq('id', update.id);
      }
    } catch (err) {
      console.error('Error reordering tasks:', err);
      setError('Failed to update task order.');

      // Refresh tasks from database on error
      fetchTasks();
    } finally {
      setIsReordering(false);
    }
  };

  return (
    <>
      <div className="relative" style={{ marginBottom: 'var(--space-6)' }}>
        <div className="flex flex-col md:flex-row md:items-center justify-between" style={{ gap: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
          <div className="flex items-center" style={{ gap: '10px' }}>
            <span className="t-eyebrow">Website Tasks</span>
            {isReordering && (
              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
                Updating priorities...
              </span>
            )}
          </div>

          <div className="flex items-center flex-wrap" style={{ gap: '8px' }}>
            {/* Status filter */}
            <Dropdown
              size="sm"
              options={[
                { value: '', label: 'All Statuses' },
                { value: 'Scheduled', label: 'Scheduled' },
                { value: 'In Progress', label: 'In Progress' },
                { value: 'Blocked', label: 'Blocked' },
                { value: 'Complete', label: 'Complete' }
              ]}
              value={statusFilter || ''}
              onChange={(e) => setStatusFilter(e.target.value || null)}
              placeholder="Status..."
              style={{ width: '160px' }}
            />

            {/* Search field */}
            <div className="w-full sm:w-48">
              <Input
                icon="funnel"
                fieldSize="sm"
                placeholder="Filter tasks..."
                value={localSearchTerm}
                onChange={(e) => setLocalSearchTerm(e.target.value)}
              />
            </div>

            {/* Add task button */}
            <Button size="sm" icon="plus" onClick={() => setIsAddingTask(true)}>
              Add Task
            </Button>
          </div>
        </div>

        {error && (
          <div
            style={{
              marginBottom: 'var(--space-4)',
              padding: '10px 14px',
              borderRadius: 'var(--radius-md)',
              background: 'var(--status-down-soft)',
              border: '1px solid rgba(229, 72, 77, 0.25)',
              color: 'var(--danger-400)',
              fontSize: 'var(--text-sm)',
            }}
          >
            {error}
          </div>
        )}

        {/* Add Task Form */}
        {isAddingTask && (
          <div
            className="ck-menu-pop"
            style={{
              marginBottom: 'var(--space-5)',
              background: 'var(--surface-raised)',
              border: '1px solid var(--border-hairline)',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--space-5)',
            }}
          >
            <div className="flex justify-between items-start" style={{ marginBottom: 'var(--space-4)' }}>
              <span className="t-eyebrow">New Task</span>
              <IconButton icon="x" size="sm" label="Close" onClick={() => setIsAddingTask(false)} />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
              {/* Website Selection */}
              <div>
                <div className="t-eyebrow" style={{ marginBottom: '6px' }}>Website*</div>
                <WebsiteDropdown
                  websites={websites}
                  selectedId={newTask.website_id}
                  onChange={(id) => setNewTask({ ...newTask, website_id: id })}
                  formatUrl={(url) => url}
                />
              </div>

              {/* Title */}
              <Input
                label="Task Title*"
                placeholder="Enter task title..."
                value={newTask.title}
                onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
              />

              {/* Description */}
              <div>
                <div className="t-eyebrow" style={{ marginBottom: '6px' }}>Description</div>
                <textarea
                  className="ck-textarea"
                  value={newTask.description}
                  onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                  placeholder="Optional task description..."
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2" style={{ gap: 'var(--space-4)' }}>
                {/* Status */}
                <Dropdown
                  label="Status"
                  options={[
                    { value: 'Scheduled', label: 'Scheduled' },
                    { value: 'In Progress', label: 'In Progress' },
                    { value: 'Complete', label: 'Complete' },
                    { value: 'Blocked', label: 'Blocked' }
                  ]}
                  value={newTask.status}
                  onChange={(e) => setNewTask({ ...newTask, status: e.target.value })}
                  placeholder="Select status..."
                />

                {/* Due Date */}
                <div>
                  <div className="t-eyebrow" style={{ marginBottom: '7px' }}>Due Date</div>
                  <input
                    type="date"
                    className="ck-date-input"
                    value={newTask.due_date}
                    onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })}
                  />
                </div>
              </div>
            </div>

            {/* Form buttons */}
            <div className="flex justify-end" style={{ gap: '8px', marginTop: 'var(--space-5)' }}>
              <Button variant="ghost" onClick={() => setIsAddingTask(false)} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button
                icon="check"
                onClick={handleAddTask}
                disabled={isSubmitting || !newTask.title.trim() || !newTask.website_id}
              >
                {isSubmitting ? 'Saving...' : 'Add Task'}
              </Button>
            </div>
          </div>
        )}

        {/* Tasks List */}
        {isLoading ? (
          <div className="flex justify-center items-center" style={{ padding: 'var(--space-14) 0' }}>
            <div
              className="w-8 h-8 rounded-full animate-spin"
              style={{ border: '3px solid var(--border-hairline)', borderTopColor: 'var(--accent)' }}
            />
          </div>
        ) : filteredTasks.length === 0 ? (
          <div
            className="text-center"
            style={{
              background: 'var(--surface-raised)',
              border: '1px solid var(--border-hairline)',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--space-8)',
            }}
          >
            <div style={{ color: 'var(--text-faint)', marginBottom: 'var(--space-3)' }}>
              <Icon name="clipboard-check" size={28} />
            </div>
            <h3
              style={{
                fontSize: 'var(--text-md)',
                fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
                color: 'var(--text-body)',
                marginBottom: 'var(--space-2)',
              }}
            >
              {statusFilter ? `No ${statusFilter} Tasks` : 'No Tasks Found'}
            </h3>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', marginBottom: 'var(--space-5)' }}>
              {localSearchTerm ? 'Try a different search term' : 'Create your first task to get started'}
            </p>
            {!isAddingTask && (
              <Button size="sm" icon="plus" onClick={() => setIsAddingTask(true)}>
                Add Task
              </Button>
            )}
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={filteredTasks.map(task => task.id)}
              strategy={verticalListSortingStrategy}
            >
              <div>
                {filteredTasks.map((task) => (
                  <SortableTaskItem
                    key={task.id}
                    task={task}
                    onClick={handleOpenTask}
                    onStatusChange={handleStatusChange}
                    onAssign={handleAssignTask}
                    onDelete={openDeleteModal}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>

      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteTask}
        isDeleting={isDeleting}
        title="Delete Task"
        message="Are you sure you want to delete this task? This action cannot be undone."
      />

      {/* Task Detail Panel */}
      <WebsiteTaskDetailModal
        isOpen={isTaskModalOpen}
        setIsOpen={setIsTaskModalOpen}
        selectedTask={selectedTask}
        setSelectedTask={setSelectedTask}
        editMode={editMode}
        setEditMode={setEditMode}
        editForm={editForm}
        setEditForm={setEditForm}
        handleSaveTaskChanges={handleSaveTaskChanges}
        handleStatusChange={handleStatusChange}
        setError={setError}
      />
    </>
  );
}
